/**
 * Sizes insight offers. Only the server sizes (`computeInsightValues`); every surface reads the offers, so
 * the number beside a switch is the number the switch produces. Pure: no React, no DB types.
 */

import type { CollectionKind } from "~/utils/collectionKind"
import {
  DEFAULT_DISCOUNT_MODE,
  DEFAULT_PHASING_OUT_MODE,
  DEFAULT_STOCKOUT_MODE,
  type DiscountMode,
  discountModeUnits,
  type PhasingOutMode,
  phasingOutModeUnits,
  type StockoutMode,
  stockoutModeUnits,
} from "~/utils/demandNodeModes"

export type NoosMetricKey =
  | "preOrderChange"
  | "unknownPreOrders"
  | "stockout"
  | "missingSales"
  | "partialHistory"
  | "manualAdjustment"
  | "excludedFromForecast"
  | "fashionForecast"
  | "phasingOut"
  | "discounts"
  | "salesTrend"
  | "seasonTrend"
  | "preorderDevelopment"
  | "modelTrend"
  | "aiImpact"
  | "npiKnownPreorders"
  | "npiForecastedDemand"

/** Sized from a level aggregate, so only coherent collection-wide. Mutations refuse them per variant. */
export const LEVEL_SCOPED_INSIGHT_KEYS = new Set<NoosMetricKey>([
  "salesTrend",
  "seasonTrend",
  "preorderDevelopment",
])

/**
 * Fashion keys applied with no stored row. Safe because each defaults to an answer that leaves the baseline
 * alone. Client (`setupSummary.ts`) and engine (`shared/insightOffers.ts`) must read this same set.
 */
const FASHION_ALWAYS_ON_INSIGHT_KEYS: ReadonlySet<NoosMetricKey> = new Set<NoosMetricKey>([
  "seasonTrend",
  "stockout",
  "discounts",
  "preorderDevelopment",
])

/**
 * NOOS keys applied with no stored row: the fashion insights plus phasing out, each answered rather
 * than switched. A phase-out's sell-down is a lifecycle effect the baseline deliberately doesn't carry,
 * so it is its own line in the breakdown (docs/helix/NOOS_PLAN_PERIOD.md rule 5), applied by default.
 */
const NOOS_ALWAYS_ON_INSIGHT_KEYS: ReadonlySet<NoosMetricKey> = new Set<NoosMetricKey>([
  "stockout",
  "discounts",
  "preorderDevelopment",
  "phasingOut",
])

export const alwaysOnInsightKeys = (kind: "fashion" | "noos"): ReadonlySet<NoosMetricKey> =>
  kind === "fashion" ? FASHION_ALWAYS_ON_INSIGHT_KEYS : NOOS_ALWAYS_ON_INSIGHT_KEYS

/** Emission order; surfaces that aggregate across variants sort by it. Keep in step with `values.push` below. */
export const INSIGHT_KEY_ORDER: readonly NoosMetricKey[] = [
  "salesTrend",
  "seasonTrend",
  "preorderDevelopment",
  "npiForecastedDemand",
  "npiKnownPreorders",
  "preOrderChange",
  "unknownPreOrders",
  "stockout",
  "missingSales",
  "partialHistory",
  "discounts",
  "manualAdjustment",
  "excludedFromForecast",
  "fashionForecast",
  "modelTrend",
  "aiImpact",
  "phasingOut",
]

/**
 * The keys each kind offers; a key absent from a kind is not offered there, never offered at zero.
 * Both kinds offer the same answered insights (stockout, discounts, pre-order development), measured
 * on exactly what the baseline stands on: fashion on its comparables, NOOS on its own reads.
 */
export const INSIGHT_KEY_KINDS_FASHION: ReadonlySet<NoosMetricKey> = new Set<NoosMetricKey>([
  "seasonTrend",
  "preorderDevelopment",
  "stockout",
  "discounts",
])

export const INSIGHT_KEY_KINDS_NOOS: ReadonlySet<NoosMetricKey> = new Set<NoosMetricKey>([
  "preOrderChange",
  "stockout",
  "discounts",
  "phasingOut",
  "seasonTrend",
  "preorderDevelopment",
  "npiKnownPreorders",
])

/**
 * Keys no kind offers: a stored row of one moves nothing. The DS-sourced ones are here because a plan
 * is baseline + insights + adjustments and never reads DS's forecast.
 */
export const RETIRED_INSIGHT_KEYS: ReadonlySet<NoosMetricKey> = new Set<NoosMetricKey>([
  "salesTrend",
  "missingSales",
  "partialHistory",
  "modelTrend",
  "aiImpact",
  "unknownPreOrders",
  "manualAdjustment",
  "excludedFromForecast",
  "fashionForecast",
  "npiForecastedDemand",
])

const INSIGHT_KEYS_BY_KIND: Record<CollectionKind, ReadonlySet<NoosMetricKey>> = {
  fashion: INSIGHT_KEY_KINDS_FASHION,
  noos: INSIGHT_KEY_KINDS_NOOS,
}

const KNOWN_INSIGHT_KEYS: ReadonlySet<string> = new Set<string>(INSIGHT_KEY_ORDER)

/** False for a key no longer defined, which a stored row may still carry. */
export const isKnownInsightKey = (key: string): key is NoosMetricKey => KNOWN_INSIGHT_KEYS.has(key)

/** No kind means an unscoped caller: every key is offered. An unknown stored key passes. */
export const offeredOnKind = (key: string, kind: CollectionKind | undefined): boolean =>
  kind == null || !isKnownInsightKey(key) || INSIGHT_KEYS_BY_KIND[kind].has(key)

/**
 * Model-sized but planner-decided: read as Strategic assumptions, not Model insights, and folded as their own
 * layer (`ProjectionLayers.strategicInsights`).
 */
// How the engine keys an insight in its attribution maps: they share a namespace with adjustment ids.
export const INSIGHT_PREFIX = "insight:"
export const insightAttributionKey = (key: string) => `${INSIGHT_PREFIX}${key}`
export const insightKeyOf = (attributionKey: string) =>
  attributionKey.slice(INSIGHT_PREFIX.length) as NoosMetricKey

const STRATEGIC_INSIGHT_KEYS: ReadonlySet<NoosMetricKey> = new Set<NoosMetricKey>(["seasonTrend"])

export const isStrategicInsightKey = (key: NoosMetricKey): boolean =>
  STRATEGIC_INSIGHT_KEYS.has(key)

/** Which setup-stage driver an insight feeds. A strategic insight sits under `signal`. */
export type InsightLayer = "preorder" | "reorder" | "signal"

export const INSIGHT_KEY_LAYER: Record<NoosMetricKey, InsightLayer> = {
  preOrderChange: "preorder",
  unknownPreOrders: "preorder",
  preorderDevelopment: "preorder",
  npiKnownPreorders: "preorder",
  salesTrend: "reorder",
  seasonTrend: "signal",
  npiForecastedDemand: "reorder",
  stockout: "reorder",
  missingSales: "reorder",
  partialHistory: "reorder",
  discounts: "reorder",
  manualAdjustment: "reorder",
  excludedFromForecast: "reorder",
  fashionForecast: "reorder",
  modelTrend: "reorder",
  aiImpact: "reorder",
  phasingOut: "reorder",
}

/**
 * The lowest hierarchy level a planner may set an insight on. Surface-only: the engine writes every insight
 * at the all-sentinels.
 */
export type AuthoringLevel = "overall" | "warehouse" | "channel" | "market" | "all"

/**
 * A tag below the grain of the key's source is a split, not a measurement. Only `SaleBaseData` carries
 * `marketId`, so only the trends reach "all".
 */
export const INSIGHT_KEY_AUTHORING_LEVEL: Record<NoosMetricKey, AuthoringLevel> = {
  preOrderChange: "overall",
  unknownPreOrders: "overall",
  preorderDevelopment: "overall",
  npiKnownPreorders: "overall",
  npiForecastedDemand: "overall",
  stockout: "warehouse",
  missingSales: "overall",
  // Measured per leaf, but the engine writes the row at the all-sentinels and splits by baseline share.
  partialHistory: "overall",
  discounts: "overall",
  manualAdjustment: "overall",
  excludedFromForecast: "overall",
  fashionForecast: "overall",
  salesTrend: "all",
  seasonTrend: "all",
  modelTrend: "overall",
  aiImpact: "overall",
  phasingOut: "overall",
}

/** The question a planner answers by switching an insight on: addends vs rival readings of one effect. */
export type InsightSlot =
  | "growth"
  | "preorder"
  | "unrealised"
  | "pricing"
  | "lifecycle"
  | "plannerSignals"

export const INSIGHT_KEY_SLOT: Record<NoosMetricKey, InsightSlot> = {
  salesTrend: "growth",
  seasonTrend: "growth",
  modelTrend: "growth",
  aiImpact: "growth",
  preOrderChange: "preorder",
  preorderDevelopment: "preorder",
  unknownPreOrders: "preorder",
  stockout: "unrealised",
  missingSales: "unrealised",
  partialHistory: "unrealised",
  discounts: "pricing",
  phasingOut: "lifecycle",
  npiForecastedDemand: "lifecycle",
  npiKnownPreorders: "lifecycle",
  manualAdjustment: "plannerSignals",
  excludedFromForecast: "plannerSignals",
  fashionForecast: "plannerSignals",
}

const INSIGHT_SLOT_ORDER: readonly InsightSlot[] = [
  "growth",
  "preorder",
  "unrealised",
  "pricing",
  "lifecycle",
  "plannerSignals",
]

/**
 * Rival answers within one slot; enabling one clears the others. An answer is a key GROUP: `modelTrend` and
 * `aiImpact` are two halves of one number, so both must go when `salesTrend` comes on. Keys in no group are
 * plain addends.
 */
export const EXCLUSIVE_SLOT_ANSWERS: Partial<
  Record<InsightSlot, readonly (readonly NoosMetricKey[])[]>
> = {
  growth: [["salesTrend"], ["seasonTrend"], ["modelTrend", "aiImpact"]],
  preorder: [["preOrderChange"], ["preorderDevelopment"]],
}

/**
 * The key set to store, each exclusive slot reduced to one answer: the newly enabled answer wins, a tie keeps
 * the first declared. Run on both client and server.
 */
export function resolveInsightSlots(
  next: Iterable<NoosMetricKey>,
  previous: Iterable<NoosMetricKey> = [],
): NoosMetricKey[] {
  const kept = new Set(next)
  const before = new Set(previous)
  for (const slot of INSIGHT_SLOT_ORDER) {
    const groups = EXCLUSIVE_SLOT_ANSWERS[slot]
    if (!groups) continue
    const present = groups.filter((group) => group.some((key) => kept.has(key)))
    if (present.length < 2) continue
    const winner =
      present.find((group) => group.some((key) => kept.has(key) && !before.has(key))) ?? present[0]!
    for (const group of present) {
      if (group === winner) continue
      for (const key of group) kept.delete(key)
    }
  }
  return [...kept]
}

export type InsightInputs = {
  isNpi: boolean
  /** The phase-out line sized on the plan's own baseline and stock, set by the engine for NOOS. */
  phaseOutSellDown?: number
  /** Confirmed pre-orders over the next 52 weeks (`ForecastVariant.knownPreOrder`). */
  futurePreOrders: number
  /** Realised pre-orders inside the displayed gross baseline. Absent: no pre-order insight. */
  baselineRealisedPreorders?: number
  /** Multiplicand for the level-scoped trends; not `sales`, so it holds when another insight toggles. */
  baseline?: number
  /** Growth off the comparable lineage. Absent: not offered. */
  seasonTrendPct?: number | null
  /**
   * Server-supplied: the stockout loss measured on what the baseline stands on (fashion: its
   * comparables; NOOS: its own sales or its matches over the weeks it reads), never DS's imputation.
   */
  comparableStockoutUnits?: number
  /** Server-supplied, measured like `comparableStockoutUnits`: full-price demand minus sold, ≤ 0. */
  discountUnits?: number
  /** Level-scoped: this variant's projected pre-orders beyond its baseline's. */
  preorderDevelopmentUnits?: number
  /** NOOS: the book `preorderDevelopmentUnits` is sized on. */
  preorderBook?: PreorderBook
  /**
   * Fashion: unconfirmed demand, what growth lands on and may consume: the baseline less its
   * pre-orders, plus the pre-orders still expected.
   */
  seasonTrendBase?: number
  /** Fashion: pre-orders confirmed now, which growth never moves. */
  confirmedPreorders?: number
  discountShare?: number
  comparableSales?: number
  discountedSales?: number
  /** `maintain` offers the row at zero. Absent reads as `remove`. */
  discountMode?: DiscountMode
  /** `ignore` offers the row at zero. Absent reads as `add`. */
  stockoutMode?: StockoutMode
  /** `ignore` offers the row at zero. Absent reads as `apply`. */
  phasingOutMode?: PhasingOutMode
  /** Absent on unscoped callers, and then nothing is filtered. */
  kind?: CollectionKind
}

type InsightValue = { key: NoosMetricKey; value: number }

/**
 * An insight's units on this variant and whether the plan applies it. A `level` row is this variant's slice of
 * `levelUnits` and reconciles back to it; a `variant` row sums plainly.
 */
export type InsightOffer = { key: NoosMetricKey; applied: boolean } & (
  | { basis: "variant"; units: number }
  | { basis: "level"; units: number; levelUnits: number }
)

/** Tags values with their basis. A level key with no level figure falls back to `variant`. */
export const toInsightOffers = (
  values: readonly InsightValue[],
  applied: (key: NoosMetricKey) => boolean,
  levelUnitsFor: (key: NoosMetricKey) => number | null,
): InsightOffer[] =>
  values.map((v) => {
    const head = { key: v.key, applied: applied(v.key) }
    if (!LEVEL_SCOPED_INSIGHT_KEYS.has(v.key)) {
      return { ...head, basis: "variant", units: v.value }
    }
    const levelUnits = levelUnitsFor(v.key)
    return levelUnits == null
      ? { ...head, basis: "variant", units: v.value }
      : { ...head, basis: "level", units: v.value, levelUnits }
  })

/** Scalars the label layer needs for its detail strings. */
export type InsightScalars = {
  futurePreOrders: number
  baselineRealisedPreorders?: number
  baseline?: number
  kind?: CollectionKind
  /** See `InsightInputs.seasonTrendBase`. */
  seasonTrendBase?: number
  confirmedPreorders?: number
  discountShare?: number
  comparableSales?: number
  discountedSales?: number
  /** ≤ 0 whatever the answer, so `maintain` can still quote it. */
  discountImpactUnits?: number
  discountMode?: DiscountMode
  /** ≥ 0 whatever the answer, so `ignore` can still quote it. */
  comparableStockoutUnits?: number
  stockoutMode?: StockoutMode
  /** ≤ 0 whatever the answer, so `ignore` can still quote it. */
  phasingOutUnits?: number
  phasingOutMode?: PhasingOutMode
  /** NOOS: the pre-order book the insight is sized on, as the breakdown draws it. */
  preorderBook?: PreorderBook
}

/** What publish keeps per variant, so the explain can name what moved an insight since. */
export type PublishedInsights = { scalars: InsightScalars }

/**
 * The scalars each insight's sizing reads (`sizeInsightValues`, and the season trend's spread in
 * `expandCollectionInsights`); a full Record, so a new key must declare its inputs.
 */
export const INSIGHT_INPUTS: Record<NoosMetricKey, readonly (keyof InsightScalars)[]> = {
  preOrderChange: ["futurePreOrders", "baselineRealisedPreorders"],
  unknownPreOrders: [],
  stockout: ["comparableStockoutUnits", "stockoutMode"],
  missingSales: [],
  partialHistory: [],
  manualAdjustment: [],
  excludedFromForecast: [],
  fashionForecast: [],
  phasingOut: ["phasingOutUnits", "phasingOutMode"],
  discounts: ["discountImpactUnits", "discountMode"],
  salesTrend: [],
  seasonTrend: ["baseline", "seasonTrendBase"],
  preorderDevelopment: ["preorderBook", "confirmedPreorders"],
  modelTrend: [],
  aiImpact: [],
  npiKnownPreorders: [],
  npiForecastedDemand: [],
}

/**
 * A NOOS variant's pre-order book: what its baseline's reads realised and had confirmed at the same
 * lead time (`lastYear`, `knownAYearAgo`), and what is confirmed now. Totals and 52 plan weeks.
 */
export type PreorderBook = {
  lastYear: number
  knownAYearAgo: number
  knownNow: number
  weeks: { lastYear: number[]; knownAYearAgo: number[]; knownNow: number[] }
  /** The variant's weeks in the plan, the weeks the book covers; absent reads as all of them. */
  weeksInPlan?: number
}

const insightScalars = (inputs: InsightInputs): InsightScalars => ({
  futurePreOrders: inputs.futurePreOrders,
  baselineRealisedPreorders: inputs.baselineRealisedPreorders,
  baseline: inputs.baseline,
  kind: inputs.kind,
  seasonTrendBase: inputs.seasonTrendBase,
  confirmedPreorders: inputs.confirmedPreorders,
  discountShare: inputs.discountShare,
  comparableSales: inputs.comparableSales,
  discountedSales: inputs.discountedSales,
  discountImpactUnits: inputs.discountUnits,
  discountMode: inputs.discountMode,
  comparableStockoutUnits: inputs.comparableStockoutUnits,
  stockoutMode: inputs.stockoutMode,
  phasingOutUnits: inputs.phaseOutSellDown ?? 0,
  phasingOutMode: inputs.phasingOutMode,
  preorderBook: inputs.preorderBook,
})

/** A variant's sell-down is offered, and so planned, only once it rounds to a unit. */
export const isPhasingOutOffered = (sellDownUnits: number): boolean =>
  Math.round(sellDownUnits) !== 0

/** A variant's stockout is offered, and so planned, only once it rounds to a unit. */
export const isStockoutOffered = (measuredUnits: number): boolean => measuredUnits > 0.5

/** A variant's discount is offered, and so planned, only once it rounds to a unit. */
export const isDiscountOffered = (measuredUnits: number): boolean => Math.abs(measuredUnits) > 0.5

/**
 * The insight keys a variant offers, sized, in presentation order. An insight that rounds to nothing is not
 * offered, so the server never folds a zero row into the setup stage.
 */
export function computeInsightValues(inputs: InsightInputs): {
  values: InsightValue[]
  scalars: InsightScalars
} {
  const sized = sizeInsightValues(inputs)
  return { ...sized, values: sized.values.filter((v) => offeredOnKind(v.key, inputs.kind)) }
}

function sizeInsightValues(inputs: InsightInputs): {
  values: InsightValue[]
  scalars: InsightScalars
} {
  const seasonTrendUnits =
    inputs.seasonTrendPct != null && inputs.baseline != null
      ? (inputs.baseline * inputs.seasonTrendPct) / 100
      : 0
  // Offered at zero units too: a custom 0% is an answer, not an absence.
  const seasonTrendOffered =
    inputs.seasonTrendPct != null && inputs.baseline != null && inputs.baseline !== 0

  // Offered at zero too: the server omits variants with no book, so zero is a real answer.
  const preorderDevelopmentUnits = inputs.preorderDevelopmentUnits
  const preorderDevelopmentOffered = preorderDevelopmentUnits != null

  // Stockout and discounts are measured on the baseline's source and offered on both branches. Offered
  // whenever measured, whatever the planner answered; the value follows the answer.
  const measuredStockoutUnits = inputs.comparableStockoutUnits ?? 0
  const stockoutOffered = isStockoutOffered(measuredStockoutUnits)
  const stockoutUnits = stockoutModeUnits(
    inputs.stockoutMode ?? DEFAULT_STOCKOUT_MODE,
    measuredStockoutUnits,
  )
  const measuredDiscountUnits = inputs.discountUnits ?? 0
  const discountsOffered = isDiscountOffered(measuredDiscountUnits)
  const discountUnits = discountModeUnits(
    inputs.discountMode ?? DEFAULT_DISCOUNT_MODE,
    measuredDiscountUnits,
  )

  const sellDown = inputs.phaseOutSellDown ?? 0

  if (inputs.isNpi) {
    const values: InsightValue[] = []
    if (seasonTrendOffered) values.push({ key: "seasonTrend", value: seasonTrendUnits })
    if (preorderDevelopmentOffered) {
      values.push({ key: "preorderDevelopment", value: preorderDevelopmentUnits })
    }
    if (stockoutOffered) values.push({ key: "stockout", value: stockoutUnits })
    if (discountsOffered) values.push({ key: "discounts", value: discountUnits })
    // A handed-off NPI sells down like any own-sales variant.
    if (isPhasingOutOffered(sellDown))
      values.push({ key: "phasingOut", value: phasingOutValue(inputs, sellDown) })
    return { values, scalars: insightScalars(inputs) }
  }

  const values: InsightValue[] = []

  if (seasonTrendOffered) values.push({ key: "seasonTrend", value: seasonTrendUnits })

  if (preorderDevelopmentOffered) {
    values.push({ key: "preorderDevelopment", value: preorderDevelopmentUnits })
  }

  // Committed pre-orders minus the realised pre-orders inside this variant's own baseline, never
  // `lastYearPreOrders` (a different definition). Withheld when `preorderDevelopment` applies: both replace
  // the same units.
  const baselineRealisedPreorders = inputs.baselineRealisedPreorders
  if (baselineRealisedPreorders != null && !preorderDevelopmentOffered) {
    const value = inputs.futurePreOrders - baselineRealisedPreorders
    if (Math.abs(value) > 0.5) values.push({ key: "preOrderChange", value })
  }
  if (stockoutOffered) values.push({ key: "stockout", value: stockoutUnits })
  if (discountsOffered) values.push({ key: "discounts", value: discountUnits })
  if (isPhasingOutOffered(sellDown))
    values.push({ key: "phasingOut", value: phasingOutValue(inputs, sellDown) })

  return { values, scalars: insightScalars(inputs) }
}

// Offered whenever sized, whatever the planner answered; the value follows the answer.
const phasingOutValue = (inputs: InsightInputs, sellDownUnits: number): number =>
  phasingOutModeUnits(inputs.phasingOutMode ?? DEFAULT_PHASING_OUT_MODE, sellDownUnits)
